import { getFarmDrawnParcelGeometries } from '../api/farms';
import { reverseGeocode as reverseGeocodeApi } from '../api';
import { getCityCoordinates } from '../config/cityCoordinates';

/** 经纬度串、纯数字、「定位 (x, y)」等，不能当行政区名称展示 */
export function looksLikeCoordinateLabel(text) {
  const s = String(text || '').trim();
  if (!s) return true;
  if (/定位\s*\(/.test(s)) return true;
  if (/坐标/.test(s)) return true;
  // 英文/中文逗号分隔的经纬度
  if (/^-?\d+(\.\d+)?\s*[,，]\s*-?\d+(\.\d+)?$/.test(s)) return true;
  if (/^-?\d+(\.\d+)?$/.test(s)) return true;
  return false;
}

function isUsableRegionLabel(text) {
  const s = String(text || '').trim();
  return Boolean(s) && !looksLikeCoordinateLabel(s);
}

function pickRegionField(...candidates) {
  for (const c of candidates) {
    if (isUsableRegionLabel(c)) return String(c).trim();
  }
  return '';
}

/** 解析农场 location「省/市/区」；坐标串视为无效 */
export function parseFarmLocationLabels(locationStr) {
  if (!locationStr || typeof locationStr !== 'string') return null;
  const parts = locationStr
    .split('/')
    .map((s) => s.trim())
    .filter(Boolean);
  if (!parts.length) return null;
  if (parts.every(looksLikeCoordinateLabel)) return null;

  let province = '';
  let city = '';
  let district = '';
  if (parts.length >= 3) {
    province = parts[0];
    city = parts[1];
    district = parts[2];
  } else if (parts.length === 2) {
    province = parts[0];
    city = parts[1];
  } else {
    province = parts[0];
    city = parts[0];
  }

  const clean = {
    province: pickRegionField(province),
    city: pickRegionField(city, province),
    district: pickRegionField(district),
  };
  if (!clean.city && !clean.province && !clean.district) return null;
  return clean;
}

async function reverseGeocode(latitude, longitude) {
  try {
    const res = await reverseGeocodeApi(latitude, longitude);
    const body = res?.data;
    const data = body?.success ? body.data : null;
    if (!data || typeof data !== 'object') return null;
    const province = pickRegionField(data.province);
    const city = pickRegionField(data.city, data.province);
    const district = pickRegionField(data.district);
    if (!province && !city && !district) return null;
    return { province, city, district };
  } catch {
    return null;
  }
}

/**
 * 解析主农场气象坐标：地块测绘中心 → location 城市坐标
 * 展示用省市区优先：农场档案 location → 逆地理；绝不返回坐标串作地名
 * @returns {Promise<{ latitude:number, longitude:number, province:string, city:string, district:string }|null>}
 */
export async function resolvePrimaryFarmWeatherLocation({ farmId, location } = {}) {
  const id = String(farmId || '').trim();
  if (!id || id === 'demo_farm') return null;

  const labelsFromFarm = parseFarmLocationLabels(location);

  try {
    const res = await getFarmDrawnParcelGeometries(id);
    const data = res?.data?.success ? res.data.data : res?.data;
    const lat = Number(data?.centerLat);
    const lng = Number(data?.centerLng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      const reversed = await reverseGeocode(lat, lng);
      return {
        latitude: lat,
        longitude: lng,
        province: pickRegionField(labelsFromFarm?.province, reversed?.province),
        city: pickRegionField(labelsFromFarm?.city, reversed?.city, labelsFromFarm?.province, reversed?.province),
        district: pickRegionField(labelsFromFarm?.district, reversed?.district),
      };
    }
  } catch {
    /* fallback to location string */
  }

  if (labelsFromFarm?.city) {
    const coords = await getCityCoordinates(labelsFromFarm.city, labelsFromFarm.province);
    if (coords?.latitude != null && coords?.longitude != null) {
      const lat = Number(coords.latitude);
      const lng = Number(coords.longitude);
      const reversed = await reverseGeocode(lat, lng);
      return {
        latitude: lat,
        longitude: lng,
        province: pickRegionField(labelsFromFarm.province, reversed?.province),
        city: pickRegionField(labelsFromFarm.city, reversed?.city),
        district: pickRegionField(labelsFromFarm.district, reversed?.district),
      };
    }
  }

  return null;
}
